import { useEffect, useState } from "react";
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  PageHeader,
} from "@/components/ui";
import {
  type AssistantConfig,
  errorMessage,
  useAssistant,
  useInvalidating,
} from "@/lib/api";
import { WidgetPreview } from "@/screens/InstallScreen";

export function AssistantScreen() {
  return (
    <>
      <PageHeader
        title="Assistant"
        subtitle="How it behaves and how it looks. Changes reach your website within a minute."
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <AssistantForm />
        <WidgetPreview />
      </div>
    </>
  );
}

const STYLE_LABELS: [number, string][] = [
  [0, "Helpful: answers the question and stops."],
  [25, "Mostly helpful: answers first, mentions contact once."],
  [50, "Balanced: answers, then asks for details when it fits."],
  [75, "Mostly conversion: short answers, steers to contact."],
  [100, "Conversion: every exchange steers toward contact details."],
];

function styleLabel(v: number): string {
  let out = STYLE_LABELS[0]?.[1] ?? "";
  for (const [at, label] of STYLE_LABELS) if (v >= at) out = label;
  return out;
}

/** The slider and the look. Shared with onboarding's Style step. */
export function AssistantForm() {
  const config = useAssistant();
  const [draft, setDraft] = useState<AssistantConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useInvalidating(
    (api, body: Partial<AssistantConfig>) => api.PATCH("/assistant", { body }),
    [["assistant"]],
  );
  useEffect(() => {
    if (config.data && !draft) setDraft(config.data);
  }, [config.data, draft]);
  if (!draft) return <Card>Loading…</Card>;

  const set = <K extends keyof AssistantConfig>(
    k: K,
    v: AssistantConfig[K],
  ) => {
    setSaved(false);
    setDraft({ ...draft, [k]: v });
  };
  const submit = async () => {
    setError(null);
    try {
      const { updatedAt: _ignored, ...body } = draft;
      await save.mutateAsync(body);
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err, "Could not save."));
    }
  };

  return (
    <Card className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between text-[12px] font-semibold">
          <span>Helpful</span>
          <span>Conversion</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={draft.responseStyle}
          onChange={(e) => set("responseStyle", Number(e.target.value))}
          className="w-full accent-[rgb(var(--color-accent))]"
        />
        <p className="text-[13px] text-muted">
          {styleLabel(draft.responseStyle)}
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Assistant name">
          <Input
            value={draft.displayName}
            maxLength={40}
            onChange={(e) => set("displayName", e.target.value)}
          />
        </Field>
        <Field label="Colour">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={draft.primaryColor}
              onChange={(e) => set("primaryColor", e.target.value)}
              className="h-10 w-14 cursor-pointer rounded-control border border-border bg-surface"
            />
            <Input
              value={draft.primaryColor}
              onChange={(e) => set("primaryColor", e.target.value)}
              pattern="^#[0-9a-fA-F]{6}$"
            />
          </div>
        </Field>
      </div>
      <Field
        label="Greeting"
        hint="The first thing a visitor sees when they open the chat."
      >
        <Input
          value={draft.greeting}
          maxLength={300}
          onChange={(e) => set("greeting", e.target.value)}
        />
      </Field>
      <Field label="Position">
        <div className="flex gap-2">
          {(["left", "right"] as const).map((p) => (
            <Button
              key={p}
              variant={draft.position === p ? "primary" : "secondary"}
              onClick={() => set("position", p)}
            >
              {p === "left" ? "Bottom left" : "Bottom right"}
            </Button>
          ))}
        </div>
      </Field>
      <ErrorNote message={error} />
      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          onClick={() => void submit()}
          disabled={save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        {saved ? (
          <span className="text-[13px] text-success">Saved.</span>
        ) : null}
      </div>
    </Card>
  );
}
